import React, { useRef, useState, useEffect } from 'react';
import './FormField.scss';
import { ReactComponent as AlertIcon } from '../../images/alert-circle.svg';
import CalendarDatepicker from './CalendarDatepicker';
import { ReactComponent as UploadFileIcon } from '../../images/upload-cloud-02.svg';
import { ReactComponent as AttachmentIcon } from '../../images/attachment-01.svg';
import { ReactComponent as TrashIcon } from '../../images/trash.svg';
import { ReactComponent as CheckCircle } from '../../images/check-circle.svg';
import Modal from '../../features/common/Modal/Modal';

interface FormFieldProps {
  label?: string;
  name: string;
  value: string | Date | null | number | boolean | File | File[] | null;
  onChange: (name: string, value: any) => void;
  onBlur?: (name: string) => void;
  helpText?: string;
  isSelect?: boolean;
  multilineHelpText?: boolean;
  containerClassName?: string;
  error?: string;
  required?: boolean;
  type?: 'text' | 'select' | 'date' | 'number' | 'file' | 'checkbox';
  disabledDates?: string[];
  disabled?: boolean;
  options?: { key: number | string; value: string }[];
  minDate?: Date;
  className?: string;
  placeholderOptionLabel?: string;
  isLoading?: boolean;
  min?: number;
  max?: number;
  accept?: string; // For file input
  multiple?: boolean; // For file input
  uploadText?: string; // Custom text for file upload
  isCheckboxRight?: boolean;
  readOnly?: boolean;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  helpText,
  multilineHelpText = false,
  containerClassName = '',
  error,
  required = false,
  type = 'text',
  disabledDates,
  disabled = false,
  options,
  minDate,
  placeholderOptionLabel,
  isLoading,
  min,
  max,
  accept,
  multiple = false,
  isCheckboxRight = false,
  readOnly = false,
}) => {
  const [localUploadingFiles, setLocalUploadingFiles] = useState<{ [fileName: string]: boolean }>({});
  const [uploadProgress, setUploadProgress] = useState<{ [fileName: string]: number }>({});
  const hasError = !!error;
  const datePickerRef = useRef<HTMLDivElement>(null);

  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
  const selectWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isSelectOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (selectWrapperRef.current && !selectWrapperRef.current.contains(e.target as Node)) {
        setIsSelectOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSelectOpen]);

  const handleBlur = () => {
    if (onBlur) {
      onBlur(name);
    }
  };

  const handleDateChange = (date: Date | null) => {
    onChange(name, date);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    const setUploading = (fileList: File[]) => {
      fileList.forEach((file) => {
        setLocalUploadingFiles((prev) => ({ ...prev, [file.name]: true }));
        setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));

        // Simulate upload progress
        let progress = 0;
        const interval = setInterval(() => {
          progress += Math.random() * 15 + 5;
          if (progress >= 100) {
            progress = 100;
            setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));
            setLocalUploadingFiles((prev) => ({ ...prev, [file.name]: false }));
            clearInterval(interval);
          } else {
            setUploadProgress((prev) => ({ ...prev, [file.name]: progress }));
          }
        }, 200);
      });
    };

    if (multiple) {
      const currentFiles = Array.isArray(value) ? value : value instanceof File ? [value] : [];
      const newFiles = files ? Array.from(files) : [];
      onChange(name, [...currentFiles, ...newFiles]);
      setUploading(newFiles);
    } else {
      const file = files?.[0] || null;
      onChange(name, file);
      if (file) setUploading([file]);
    }
  };

  const confirmDeleteFile = () => {
    if (pendingDeleteIndex !== null) {
      onChange(name, Array.isArray(value) ? value.filter((_, i) => i !== pendingDeleteIndex) : null);
    } else {
      onChange(name, null);
    }

    setPendingDeleteIndex(null);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(name, e.target.checked);
  };

  return (
    <div className={`form-field ${containerClassName}`}>
      <div className='field-container'>
        {label && type !== 'checkbox' && (
          <label htmlFor={name} className='field-label'>
            {label} {required && <span className={`required-indicator  ${hasError ? 'error' : ''}`}>*</span>}
          </label>
        )}
        <div className={`input-wrapper ${type === 'number' && hasError ? 'has-number-error' : ''}`}>
          {type === 'select' ? (
            <div ref={selectWrapperRef} className={`select-field-wrapper ${isSelectOpen ? 'is-open' : ''}`}>
              <select
                id={name}
                name={name}
                value={value as string}
                onChange={(e) => onChange(name, e.target.value)}
                onBlur={handleBlur}
                onClick={() => setIsSelectOpen((prev) => !prev)}
                className={`field-select ${hasError ? 'error' : ''}`}
                disabled={disabled}
              >
                {isLoading && (
                  <option value='' disabled>
                    Loading...
                  </option>
                )}
                {!isLoading && options?.length === 0 && (
                  <option value='' disabled>
                    No data found
                  </option>
                )}
                {options && options?.length > 0 && (
                  <>
                    <option value=''>{placeholderOptionLabel || 'Select'}</option>
                    {options?.map((opt: { key: string | number; value: string }) => (
                      <option key={opt.key} value={opt.key}>
                        {opt.value}
                      </option>
                    ))}
                  </>
                )}
              </select>
              <span className={`dropdown-icon ${isSelectOpen ? 'open' : ''}`} />
            </div>
          ) : type === 'date' ? (
            <div ref={datePickerRef}>
              <CalendarDatepicker
                selectedDate={value as Date | null}
                setSelectedDate={handleDateChange}
                datepickerOnChange={(value) => handleDateChange(value?.startDate ? new Date(value.startDate) : null)}
                error={hasError}
                disabledDates={disabledDates}
                disabled={disabled || readOnly}
                minDate={minDate}
                triggerRef={datePickerRef}
              />
            </div>
          ) : type === 'file' ? (
            <div className='file-upload-wrapper'>
              <input
                id={name}
                type='file'
                name={name}
                onChange={handleFileChange}
                onBlur={handleBlur}
                className={`file-input-hidden`}
                disabled={disabled || readOnly}
                accept={accept}
                multiple={multiple}
                style={{ display: 'none' }}
              />
              <div
                className={`file-upload-custom-box ${hasError ? 'error' : ''}`}
                onClick={() => {
                  if (!(disabled || readOnly)) {
                    document.getElementById(name)?.click();
                  }
                }}
                role='button'
                tabIndex={0}
                style={{ cursor: disabled || readOnly ? 'not-allowed' : 'pointer' }}
              >
                <div className='upload-icon-row'>
                  <div className='upload-icon-border'>
                    <UploadFileIcon />
                  </div>
                </div>
                <div className='upload-text-row'>
                  <span
                    className={`upload-click-text${disabled || readOnly ? ' disabled' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!(disabled || readOnly)) {
                        document.getElementById(name)?.click();
                      }
                    }}
                  >
                    Click to upload
                  </span>
                  <span className='upload-drag-text'> or drag and drop</span>
                </div>
                <div className='upload-info-row'>
                  <span className='upload-info-text'>{placeholderOptionLabel}</span>
                </div>
              </div>
              {/* Show uploaded files */}
              {(() => {
                const formatSize = (size: number) => {
                  if (size < 1024) return `${size} B`;
                  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
                  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
                };
                const renderFileBox = (file: File, idx?: number) => {
                  const isUploading = !!localUploadingFiles[file.name];
                  const progress = uploadProgress[file.name] || 0;
                  const displayProgress = isUploading ? Math.round(progress) : 100;

                  return (
                    <div key={`${file.name}-${idx ?? ''}`} className={`uploaded-file-box ${hasError ? 'error' : ''}`}>
                      <div className='uploaded-file-first-line'>
                        <AttachmentIcon className='uploaded-file-icon' />
                        <div className='uploaded-file-name-section'>
                          <span className='uploaded-file-name'>{file.name}</span>
                        </div>
                        <button
                          type='button'
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPendingDeleteIndex(idx ?? null);
                            setShowConfirmModal(true);
                          }}
                          className='clear-file-btn'
                          title='Remove file'
                          disabled={disabled || readOnly}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                      <div className='uploaded-file-second-line'>
                        <div className='uploaded-file-status-section'>
                          <span className='uploaded-file-size'>
                            {formatSize(file.size)} of {formatSize(file.size)}
                          </span>
                          <span className='status-separator'>|</span>
                          {isUploading ? (
                            <span className='upload-status uploading'>
                              <UploadFileIcon className='status-icon' />
                              Uploading...
                            </span>
                          ) : (
                            <span className='upload-status complete'>
                              <CheckCircle className='status-icon' />
                              Complete
                            </span>
                          )}
                        </div>
                        <span className='progress-percentage'>{displayProgress}%</span>
                      </div>

                      {/* Progress bar */}
                      <div className='progress-bar-container'>
                        <div className='progress-bar'>
                          <div
                            className={`progress-bar-fill ${isUploading ? 'uploading' : 'complete'}`}
                            style={{ width: `${displayProgress}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                };
                if (multiple && Array.isArray(value)) {
                  const files = value.filter((f) => f instanceof File);
                  if (files.length > 0) {
                    return (
                      <div className='uploaded-files-list'>{files.map((file, idx) => renderFileBox(file, idx))}</div>
                    );
                  }
                } else if (!multiple && value instanceof File && value) {
                  return renderFileBox(value);
                }
                return null;
              })()}
            </div>
          ) : type === 'checkbox' ? (
            isCheckboxRight ? (
              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <div
                  className={`checkbox ${value ? 'enabled' : ''}`}
                  onClick={() => !(disabled || readOnly) && onChange(name, !value)}
                  style={{ cursor: disabled || readOnly ? 'not-allowed' : 'pointer' }}
                >
                  {value && <div className='checkbox-icon' />}
                </div>
                <span
                  style={{
                    marginLeft: 8,
                    cursor: disabled || readOnly ? 'not-allowed' : 'pointer',
                    opacity: disabled || readOnly ? 0.5 : '',
                  }}
                  onClick={() => !(disabled || readOnly) && onChange(name, !value)}
                >
                  {label} {required && <span className={`required-indicator ${hasError ? 'error' : ''}`}>*</span>}
                </span>
              </div>
            ) : (
              <div className='checkbox-wrapper'>
                <label htmlFor={name} className={`checkbox-label ${hasError ? 'error' : ''}`}>
                  {label && (
                    <span className='checkbox-text'>
                      {label} {required && <span className={`required-indicator ${hasError ? 'error' : ''}`}>*</span>}
                    </span>
                  )}
                  <span className='checkbox-custom'></span>
                </label>
                <input
                  id={name}
                  type='checkbox'
                  name={name}
                  checked={value as boolean}
                  onChange={handleCheckboxChange}
                  onBlur={handleBlur}
                  className={`checkbox-input ${hasError ? 'error' : ''}`}
                  disabled={disabled || readOnly}
                />
              </div>
            )
          ) : (
            <div>
              <input
                id={name}
                type={type}
                name={name}
                disabled={disabled}
                readOnly={readOnly}
                value={value as string}
                onChange={(e) => onChange(name, e.target.value)}
                onBlur={handleBlur}
                className={`field-input ${hasError ? 'error' : ''}`}
                placeholder=''
                min={type === 'number' ? min : undefined}
                max={type === 'number' ? max : undefined}
              />
            </div>
          )}
          {hasError && type !== 'date' && type !== 'select' && type !== 'file' && type !== 'checkbox' && (
            <div
              className={`absolute ${type === 'number' ? 'error-indicator-number' : 'right-[12px]'
                } top-1/2 transform -translate-y-1/2 `}
            >
              <AlertIcon width='16' height='16' />
            </div>
          )}
        </div>
      </div>
      {multilineHelpText ? (
        <span className={`help-text multiline ${hasError ? 'error' : ''}`}>
          {error || (helpText?.split(' and ')[0] || '') + ' and '}
          <br />
          {!error && (helpText?.split(' and ')[1] || '')}
        </span>
      ) : (
        <span className={`help-text ${hasError ? 'error' : ''}`}>{error || helpText || ''}</span>
      )}
      <Modal
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        callbackOnConfirm={confirmDeleteFile}
        title='Confirm delete'
        message='Are you sure you want to delete this document?'
        buttonText='Yes'
        hideCancelButton={false}
        buttonColor='red'
      />
    </div>
  );
};

export default FormField;
